import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Camera, Trash2, Upload, X, User } from 'lucide-react'
import { useToast } from 'contexts/ToastContext'
import { useTranslation } from 'react-i18next'

interface AvatarSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  currentAvatarUrl?: string
  userName?: string
  onAvatarUpload: (file: File) => Promise<void>
  onAvatarDelete: () => Promise<void>
  isUploading?: boolean
}

export function AvatarSelectionModal({
  isOpen,
  onClose,
  currentAvatarUrl,
  userName = 'User',
  onAvatarUpload,
  onAvatarDelete,
  isUploading = false
}: AvatarSelectionModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const getAvatarUrl = () => {
    return previewUrl || currentAvatarUrl || ''
  }

  const validateFile = (file: File): string | null => {
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      return 'File size must be less than 5MB'
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      return 'Please select an image file'
    }

    return null
  }

  const handleFileSelect = async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      addToast({
        type: 'error',
        title: 'Invalid File',
        message: validationError,
        duration: 5000
      })
      return
    }

    setSelectedFile(file)
    
    // Create preview URL
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
  }

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    console.log('Dropzone onDrop called:', { acceptedFiles, rejectedFiles })
    const file = acceptedFiles[0]
    if (file) {
      console.log('Processing accepted file:', file.name, file.type, file.size)
      handleFileSelect(file)
    } else if (rejectedFiles.length > 0) {
      console.log('File rejected:', rejectedFiles[0].errors)
      addToast({
        type: 'error',
        title: 'Invalid File',
        message: rejectedFiles[0].errors[0]?.message || 'File type not supported',
        duration: 5000
      })
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp']
    },
    multiple: false,
    disabled: isUploading,
    noClick: false,
    noKeyboard: false
  })

  const handleUpload = async () => {
    if (!selectedFile) return

    try {
      await onAvatarUpload(selectedFile)
      handleClose()
    } catch (error) {
      // Error handling is done in parent component
    }
  }

  const handleDelete = async () => {
    try {
      await onAvatarDelete()
      handleClose()
    } catch (error) {
      // Error handling is done in parent component
    }
  }

  const handleClose = () => {
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setPreviewUrl(null)
    setSelectedFile(null)
    onClose()
  }


  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title={t('header.changeAvatar')}
      className="max-w-md"
    >
      <div className="space-y-6">
        {/* Current Avatar Preview */}
        <div className="flex justify-center">
          <div className="relative">
            <div className="h-32 w-32 rounded-full overflow-hidden ring-4 ring-border bg-muted flex items-center justify-center">
              {getAvatarUrl() ? (
                <img
                  src={getAvatarUrl()}
                  alt={userName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <User className="h-12 w-12 text-muted-foreground" />
              )}
            </div>
            {previewUrl && (
              <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
                <Upload className="h-3 w-3" />
              </div>
            )}
          </div>
        </div>

        {/* Upload Area */}
        <div
          {...getRootProps()}
          className={`
            relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200
            ${isDragActive 
              ? 'border-primary bg-primary/5 scale-[1.02]' 
              : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
            }
            ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{ minHeight: '120px' }}
        >
          <input {...getInputProps()} />
          
          <div className="flex flex-col items-center space-y-3">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
              isDragActive 
                ? 'bg-primary/10' 
                : 'bg-muted'
            }`}>
              <Camera className={`h-5 w-5 ${
                isDragActive 
                  ? 'text-primary' 
                  : 'text-muted-foreground'
              }`} />
            </div>
            
            {isDragActive ? (
              <div>
                <p className="text-sm font-medium text-primary">{t('header.dropImageHere')}</p>
                <p className="text-xs text-primary/70">{t('header.releaseToUpload')}</p>
              </div>
            ) : selectedFile ? (
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">✅ {selectedFile.name}</p>
                <p className="text-xs text-green-500 dark:text-green-300">{t('header.readyToUpload')}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedFile(null)
                    if (previewUrl) {
                      URL.revokeObjectURL(previewUrl)
                      setPreviewUrl(null)
                    }
                  }}
                  className="mt-2 text-xs"
                >
                  <X className="h-3 w-3 mr-1" />
                  {t('common.delete')}
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium">{t('header.selectNewAvatar')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('header.dragDropImage')} <span className="text-primary font-medium">{t('header.clickToBrowse')}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('header.supportedFormats')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleClose}
            disabled={isUploading}
          >
            {t('common.cancel')}
          </Button>
          
          {selectedFile && (
            <Button 
              onClick={handleUpload}
              disabled={isUploading}
              className="min-w-[100px]"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  {t('header.uploading')}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('header.upload')}
                </>
              )}
            </Button>
          )}
          
          {currentAvatarUrl && !selectedFile && (
            <Button
              onClick={handleDelete}
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
              disabled={isUploading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('header.removeAvatar')}
            </Button>
          )}
        </div>

        {/* Info Text */}
        <p className="text-xs text-muted-foreground text-center">
          {t('header.avatarFormats')}
        </p>
      </div>
    </Modal>
  )
}
